import {Button} from '@/components/ui/button';
import {Edit, Trash2, Code, Languages, Loader2, AlertTriangle, CheckCircle2} from 'lucide-react';
import {Reward, RewardRetranslateProgress} from '@/types/rewards';
import {Language} from '../types';

interface RewardCardProps {
    reward: Reward;
    availableLanguages: Language[];
    onEdit: (reward: Reward) => void;
    onDelete: (id: number) => void;
    onShowCode: (reward: Reward) => void;
    onRetranslate: (reward: Reward) => void;
    isDeleting: boolean;
    isRetranslating: boolean;
    // true enquanto QUALQUER offerwall está sendo retraduzido: uma por vez.
    retranslateDisabled?: boolean;
    retranslateProgress?: RewardRetranslateProgress | null;
}

const RetranslateProgressBar = ({
                                    progress,
                                    availableLanguages,
                                }: {
    progress: RewardRetranslateProgress;
    availableLanguages: Language[];
}) => {
    const total = Math.max(progress.total, 1);
    const pct = Math.min(100, Math.round((progress.done / total) * 100));
    const failed = progress.failed ?? [];
    const okCount = Math.max(0, progress.done - new Set(failed.map(f => f.language)).size);
    const languageName = (code: string) =>
        availableLanguages.find(l => l.code === code)?.language ?? code;

    const running = progress.status === 'running';
    const isError = progress.status === 'error';
    const barColor = isError ? 'bg-red-500' : failed.length > 0 ? 'bg-amber-500' : 'bg-purple-500';

    return (
        <div className="mb-4 rounded-md border border-gray-100 bg-gray-50 p-3 space-y-2">
            <div className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1.5 font-medium text-gray-700">
                    {running && <Loader2 className="h-3.5 w-3.5 animate-spin text-purple-600"/>}
                    {!running && !isError && failed.length === 0 && <CheckCircle2 className="h-3.5 w-3.5 text-green-600"/>}
                    {!running && (isError || failed.length > 0) && <AlertTriangle className="h-3.5 w-3.5 text-amber-600"/>}
                    {running ? 'Retraduzindo…' : isError ? 'Retradução falhou' : failed.length > 0 ? 'Concluído com erros' : 'Retradução concluída'}
                </span>
                <span className="tabular-nums text-gray-600">
                    {progress.done}/{progress.total} idiomas
                </span>
            </div>

            <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                <div
                    className={`h-2 rounded-full transition-all duration-500 ${barColor}`}
                    style={{width: `${pct}%`}}
                />
            </div>

            <div className="flex items-center justify-between text-[11px] text-gray-500">
                <span>{okCount} ok · {failed.length} com erro</span>
                {progress.tokens > 0 && <span>{progress.tokens.toLocaleString('pt-BR')} tokens</span>}
            </div>

            {isError && progress.error && (
                <p className="text-[11px] text-red-600">{progress.error}</p>
            )}

            {failed.length > 0 && (
                <div className="flex flex-wrap gap-1">
                    {failed.map((f, i) => (
                        <span
                            key={`${f.language}-${f.stage}-${i}`}
                            title="Falhou: a tradução anterior desse idioma foi mantida"
                            className="text-[11px] px-2 py-0.5 rounded-full bg-red-100 text-red-700"
                        >
                            {languageName(f.language)}{f.stage === 'quiz' ? ' (quiz)' : ''}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

export const RewardCard = ({
                               reward,
                               availableLanguages,
                               onEdit,
                               onDelete,
                               onShowCode,
                               onRetranslate,
                               isDeleting,
                               isRetranslating,
                               retranslateDisabled = false,
                               retranslateProgress = null
                           }: RewardCardProps) => {
    return (
        <div className="rounded-lg bg-white p-8 shadow border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900">{reward.title}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            reward.type === 'quiz'
                                ? 'bg-purple-100 text-purple-700'
                                : 'bg-green-100 text-green-700'
                        }`}>
                            {reward.type === 'quiz' ? 'Quiz' : 'Standard'}
                        </span>
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2">{reward.description}</p>
                </div>
            </div>

            {reward.languages && reward.languages.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-4">
                    {reward.languages.map((langCode) => {
                        const lang = availableLanguages.find(l => l.code === langCode);
                        return lang ? (
                            <span
                                key={langCode}
                                className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full"
                            >
                                {lang.language}
                            </span>
                        ) : null;
                    })}
                </div>
            )}

            {retranslateProgress && (
                <RetranslateProgressBar
                    progress={retranslateProgress}
                    availableLanguages={availableLanguages}
                />
            )}

            <div className="space-y-2 text-sm mb-4">
                <div className="flex items-center gap-2">
                    <div
                        className="w-4 h-4 rounded border border-gray-300"
                        style={{backgroundColor: reward.button_color || '#3b82f6'}}
                    />
                    <span className="text-gray-600">Cor do botão</span>
                </div>
                <div className="flex items-center gap-2">
                    <div
                        className="w-4 h-4 rounded border border-gray-300"
                        style={{backgroundColor: reward.background_color || '#ffffff'}}
                    />
                    <span className="text-gray-600">Cor de fundo</span>
                </div>
            </div>

            <div className="flex gap-2 pt-4 border-t">
                <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => onEdit(reward)}
                >
                    <Edit className="h-4 w-4 mr-2"/>
                    Editar
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onShowCode(reward)}
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                >
                    <Code className="h-4 w-4"/>
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onRetranslate(reward)}
                    disabled={isRetranslating || retranslateDisabled}
                    title={retranslateDisabled && !isRetranslating
                        ? 'Aguarde a retradução em andamento terminar'
                        : 'Retraduzir nos idiomas selecionados'}
                    className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                >
                    {isRetranslating
                        ? <Loader2 className="h-4 w-4 animate-spin"/>
                        : <Languages className="h-4 w-4"/>}
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(reward.id)}
                    disabled={isDeleting}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                    <Trash2 className="h-4 w-4"/>
                </Button>
            </div>
        </div>
    );
};